import { x25519 } from '@noble/curves/ed25519';
import {
  decodeBase64Url,
  encodeBase64Url,
  encryptAesGcm,
  decryptAesGcm,
  importAesKey,
  packEncryptedBlob,
  unpackEncryptedBlob,
  randomBytes,
  sha256,
} from './primitives';
import type { SharedFolderGuestFragment } from './sharedFolderGuestFragment';
import { buildSharedFolderGuestFragment } from './sharedFolderGuestFragment';
import { generateEphemeralKeyPair, unwrapKey, wrapKey } from './wrappedKey';

const PACKAGE_AAD_PREFIX = 'shareweb:quick-share:v1';

export interface QuickSharePackageWire {
  guestPath: string;
  wrappedPackageKey: string;
  ciphertext: string;
}

export async function buildQuickShareContextAad(
  sessionId: string,
  recipientPublicKey: Uint8Array,
): Promise<string> {
  const digest = await sha256(recipientPublicKey);
  const hashHex = Array.from(digest.slice(0, 8))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `${PACKAGE_AAD_PREFIX}|${sessionId}|${hashHex}`;
}

export async function sealQuickShareFragmentPackage(input: {
  sessionId: string;
  guestPublicKey: Uint8Array;
  fragment: SharedFolderGuestFragment;
  guestPath: string;
}): Promise<QuickSharePackageWire> {
  if (input.guestPublicKey.length !== 32) {
    throw new Error('invalid_guest_public_key');
  }

  const aad = await buildQuickShareContextAad(input.sessionId, input.guestPublicKey);
  const packageKey = randomBytes(32);
  const wrappedPackageKey = await wrapKey(packageKey, input.guestPublicKey, aad);
  const plaintext = new TextEncoder().encode(JSON.stringify(input.fragment));
  const aesKey = await importAesKey(packageKey);
  const encrypted = await encryptAesGcm(aesKey, plaintext, new TextEncoder().encode(aad));
  const packed = packEncryptedBlob(encrypted);

  return {
    guestPath: input.guestPath,
    wrappedPackageKey: encodeBase64Url(wrappedPackageKey),
    ciphertext: encodeBase64Url(packed),
  };
}

export async function openQuickShareFragmentPackage(input: {
  sessionId: string;
  guestPrivateKey: Uint8Array;
  wrappedPackageKey: string;
  ciphertext: string;
}): Promise<SharedFolderGuestFragment> {
  const guestPublicKey = x25519.getPublicKey(input.guestPrivateKey);
  const aad = await buildQuickShareContextAad(input.sessionId, guestPublicKey);
  const packageKey = await unwrapKey(
    decodeBase64Url(input.wrappedPackageKey),
    input.guestPrivateKey,
    aad,
  );
  const aesKey = await importAesKey(packageKey);
  const plaintext = await decryptAesGcm(
    aesKey,
    unpackEncryptedBlob(decodeBase64Url(input.ciphertext)),
    new TextEncoder().encode(aad),
  );
  const fragment = JSON.parse(new TextDecoder().decode(plaintext)) as SharedFolderGuestFragment;
  if (fragment.v !== 1 || !fragment.fs || !fragment.gw) {
    throw new Error('invalid_fragment_package');
  }
  return fragment;
}

export function buildGuestUrlFromPackage(
  guestPath: string,
  fragment: SharedFolderGuestFragment,
  origin: string = typeof window !== 'undefined' ? window.location.origin : '',
): string {
  const encoded = buildSharedFolderGuestFragment(fragment);
  return `${origin}${guestPath}#${encoded}`;
}

export { generateEphemeralKeyPair };
